/* Shared carousel track — one behavior for every content carousel on the site.
   Matches the homepage editorial `.jc-track`: native horizontal scroll-snap
   plus mouse drag-scroll. Navigation is drag + snap (the homepage hides arrow
   chrome); optional prev/next buttons are supported and scroll by one item.

   Registers as the <jc-carousel> custom element and inits on connect, so
   sections injected after load (e.g. AJAX product recommendations) work with no
   extra wiring. Markup contract (see snippets/carousel-track.liquid):

     <jc-carousel data-drag>
       [<button data-jc-prev>…</button>]
       <div class="jc-track" data-jc-track> …items… </div>
       [<button data-jc-next>…</button>]
     </jc-carousel>

   The drag-scroll logic is the shared version of the per-section script that
   used to be copy-pasted into jc-editorial-pillars / jc-article-return etc. */
class JcCarousel extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this.track = this.querySelector('[data-jc-track]') || this.querySelector('.jc-track');
    if (!this.track) return;
    this._init = true;

    const prev = this.querySelector('[data-jc-prev]');
    const next = this.querySelector('[data-jc-next]');
    if (prev) prev.addEventListener('click', () => this.track.scrollBy({ left: -this.step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => this.track.scrollBy({ left: this.step(), behavior: 'smooth' }));

    if (this.hasAttribute('data-drag')) this.enableDragScroll();
  }

  // Distance to advance for one arrow click: first item's width + the track gap.
  step() {
    const item = this.track.firstElementChild;
    if (!item) return this.track.clientWidth * 0.8;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '24') || 24;
    return item.getBoundingClientRect().width + gap;
  }

  // Mouse drag-scroll. Pointer (not touch — native touch scroll already works),
  // with a 5px threshold before it counts as a drag, and a one-shot capture-phase
  // click suppressor so a drag doesn't trigger the card link underneath.
  enableDragScroll() {
    const scroller = this.track;
    let down = false, dragged = false, startX = 0, startScroll = 0, pid = null;
    scroller.style.cursor = 'grab';

    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      down = true; dragged = false; pid = e.pointerId;
      startX = e.clientX; startScroll = scroller.scrollLeft;
      scroller.style.cursor = 'grabbing';
    });
    scroller.addEventListener('pointermove', (e) => {
      if (!down || e.pointerId !== pid) return;
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) > 5) {
        dragged = true;
        try { scroller.setPointerCapture(pid); } catch (_) {}
      }
      if (dragged) { e.preventDefault(); scroller.scrollLeft = startScroll - dx; }
    });
    const end = () => {
      if (!down) return;
      down = false;
      scroller.style.cursor = 'grab';
      if (dragged) {
        const suppress = (ev) => { ev.preventDefault(); ev.stopPropagation(); scroller.removeEventListener('click', suppress, true); };
        scroller.addEventListener('click', suppress, true);
      }
      try { if (pid !== null) scroller.releasePointerCapture(pid); } catch (_) {}
      pid = null;
    };
    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);
    scroller.addEventListener('pointerleave', end);
  }
}

if (!customElements.get('jc-carousel')) {
  customElements.define('jc-carousel', JcCarousel);
}
